import { createStore } from 'vuex';
import api from '@/utils/axios';

let pingInterval: number | null = null;

function startPing() {
  if (pingInterval) clearInterval(pingInterval);

  pingInterval = window.setInterval(() => {
    api
      .post('auth/ping-session/', {}, { skipLoader: true, isPing: true } as any)
      .catch(() => {
        // ignore silently
      });
  }, 2 * 60 * 1000);
}

function stopPing() {
  if (pingInterval) clearInterval(pingInterval);
  pingInterval = null;
}

export default createStore({
  state: {
    access: null as string | null, // ✅ memory only
    user: null as any,
    isAuthenticated: false,
    hasLicenseSession: false,
    peppolActive: false,
    loading: false,
  },

  mutations: {
    SET_TOKEN(state, access: string) {
      state.access = access;
      state.isAuthenticated = true;

      // Save token in browser storage
      localStorage.setItem('access', access);

      // Attach token to Axios
      api.defaults.headers.common.Authorization = `Bearer ${access}`;
    },

    CLEAR_TOKENS(state) {
      state.access = null;
      state.user = null;
      state.isAuthenticated = false;
      state.hasLicenseSession = false;

      // Remove token from storage
      localStorage.removeItem('access');

      delete api.defaults.headers.common.Authorization;
      stopPing();
    },

    SET_USER(state, user) {
      state.user = user;
    },

    SET_HAS_LICENSE_SESSION(state, value: boolean) {
      state.hasLicenseSession = !!value;
      if (state.hasLicenseSession) startPing();
      else stopPing();
    },
    setPeppolActive(state, value) {
      state.peppolActive = value;
    },
    SET_LOADING(state, value: boolean) {
      state.loading = value;
    },
  },

  getters: {
    isLogged: (s) => s.isAuthenticated,
    currentUser: (s) => s.user,
  },

  actions: {
    async logIn({ commit, dispatch }, credentials) {
      const { data } = await api.post('auth/jwt/create/', credentials);

      // ✅ only access token, memory only
      commit('SET_TOKEN', data.access);

      await dispatch('fetchCurrentUser');

      try {
        await api.post('auth/login-license/');
        commit('SET_HAS_LICENSE_SESSION', true);
      } catch (e: any) {
        const msg = e?.response?.data?.detail || e.message || 'License error';
        console.warn('login-license failed:', msg);
        commit('CLEAR_TOKENS');
        throw new Error(msg);
      }
    },

    async logOut({ commit }) {
      try {
        await api.post('auth/logout-session/');
      } catch {
        // ignore
      } finally {
        commit('CLEAR_TOKENS');
      }
    },

    async fetchCurrentUser({ commit }) {
      try {
        const res = await api.get('auth/users/me/');
        commit('SET_USER', res.data);
        return true;
      } catch (err) {
        console.error('Failed to fetch current user', err);
        commit('SET_USER', null);
        return false;
      }
    },
    async fetchPeppolStatus({ commit }) {
      const res = await api.get('/organizations/me/peppol/status/');
      commit('setPeppolActive', res.data.peppol_active);
    },

    async restoreSession({ commit, dispatch }) {
      const token = localStorage.getItem('access');

      if (!token) return;

      // Restore token in store
      commit('SET_TOKEN', token);

      try {
        // Reload user
        await dispatch('fetchCurrentUser');

        // Restore license session
        await api.post('auth/login-license/');
        commit('SET_HAS_LICENSE_SESSION', true);

        // Reload peppol status
        await dispatch('fetchPeppolStatus');
      } catch (err) {
        console.warn('Session restore failed');
        commit('CLEAR_TOKENS');
      }
    },
  },
});
